import { ListItemText } from "@mui/material";
import { useMemo, useState } from "react";
import { CoreMenuButton, CoreMenuItem } from "@verity/components/core";
import i18n from "../../../../../i18n";
import { VideoEvent } from "../../../../utilities/useTimestamps";
import { CreateCustomEventModal, CustomEventOptions } from "./CustomEventModal";
import { InstalledExtensionsModal } from "./InstalledExtensionsModal";
import { RunningApplicationsModal } from "./RunningApplicationsModal";
import { useEventDownloads } from "./timestamp-download-utils";
import DeleteAttemptModal from "../modals/DeleteAttemptModal";
import PDFReportMenuItems from "../../../PDFGenerator/PDFReportMenuItems";
import { Report, ReportProps, usePDFReportGenerator } from "../../../PDFGenerator";

/**
 * Wiring for the menu's "add new event" item. The save itself stays with the caller (TimestampLinks),
 * which owns both the event time this is anchored to and the highlight applied to the new table row.
 */
export type CreateEventMenuProps = {
	/** Pauses playback before the modal opens, so the event isn't timestamped against a moving video. */
	pausePlayback: () => void;
	/** The video timestamp the new event will be logged at, formatted for display in the modal. */
	displayedTime: string;
	onSave: (options: CustomEventOptions) => Promise<void>;
};

/**
 * Wiring for the menu's "Delete Attempt" item. The delete itself stays with the caller
 * (ProctoringResultView), which owns the attempt/test-taker selection this has to move on to afterwards.
 */
export type DeleteAttemptMenuProps = {
	/** 1-indexed attempt number, as shown in the attempt selector. */
	attemptNumber: number;
	studentName: string;
	onDelete: () => Promise<void>;
};

type EventsDownloadMenuProps = {
	events: VideoEvent[];
	offsetGetter: number;
	duration: number;
	assessmentName: string;
	studentName: string;
	sessionId: string;
	courseName?: string;
	attemptNumber?: number;
	assessmentStatus?: string;
	recordingDate?: string;
	/**
	 * When provided, renders a "Generate/Download Report" menu item (plus "Regenerate Report" once
	 * generated) at the top of the menu, immediately adjacent to each other.
	 */
	reportProps?: ReportProps;
	/**
	 * When provided, renders an "Add New Event" item that opens the create-custom-event modal — used by
	 * the LMS layout, which dropped the standalone create-event button from the events toolbar in favor
	 * of this item.
	 */
	createEventProps?: CreateEventMenuProps;
	/**
	 * When provided, renders an always-present "Delete Attempt" item at the bottom of the menu, styled as
	 * the menu's destructive action. Withheld in read-only views, which have nothing that writes.
	 */
	deleteAttemptProps?: DeleteAttemptMenuProps;
	/**
	 * Whether to render with the bordered/background box look used inline in the events table's own
	 * toolbar. Pass `false` for a bare icon look (e.g. when rendered elsewhere, like a page header).
	 * @default true
	 */
	boxed?: boolean;
	/**
	 * Pass this when the caller (e.g. TimestampLinks, which renders this menu nested inside its own
	 * toolbar) already fetched transcripts for its own purposes — skips this component's own data-dump
	 * fetch so the same session's transcripts aren't fetched twice.
	 */
	transcripts?: string[];
};

/**
 * The "download session data" kebab menu (report / keystrokes / app activity / audio transcript /
 * installed extensions). Extracted out of TimestampLinks so it can also be rendered elsewhere (e.g. the
 * new LMS layout's header row) — self-contained rather than prop-drilled, since it owns its own menu-open
 * state, modal state, and computed availability, matching what TimestampLinks already did inline.
 */
export default function EventsDownloadMenu({
	events,
	offsetGetter,
	duration,
	assessmentName,
	studentName,
	sessionId,
	courseName,
	attemptNumber,
	assessmentStatus,
	recordingDate,
	reportProps,
	createEventProps,
	deleteAttemptProps,
	boxed = true,
	transcripts,
}: EventsDownloadMenuProps) {
	const [extensionModalOpen, setExtensionModalOpen] = useState(false);
	const [runningApplicationsModalOpen, setRunningApplicationsModalOpen] = useState(false);
	const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
	const [deleteAttemptModalOpen, setDeleteAttemptModalOpen] = useState(false);
	const [downloadMenuShowing, setDownloadMenuShowing] = useState(false);

	// Owned here (rather than inside PDFReportMenuItems) so report-generation progress survives closing
	// and reopening the kebab menu — PDFReportMenuItems only exists in the DOM while the menu is open
	// (see CoreMenuButton), so any state living there would reset every time the menu closes.
	// sessionId is passed as the report identity directly (same value already flowing to this component,
	// same as it does to StudentSelector/AttemptSelectorNew) so a student/attempt switch resets progress
	// instead of leaving a stale generation/download to finish against the wrong report.
	const reportGenerator = usePDFReportGenerator(reportProps ? <Report {...reportProps} /> : null, sessionId);

	const installedExtensions = useMemo(() => {
		const extensionEvent = events.find(
			(event) => event.type === "system" && event.name === "installed extensions snapshot",
		);
		const extensions = extensionEvent?.details?.extensions as { id: string; name: string }[] | undefined;

		return Array.isArray(extensions) ? extensions.map((extension) => extension.name) : [];
	}, [events]);

	const runningApplications = useMemo(() => {
		const applicationsEvent = events.find(
			(event) => event.type === "system" && event.name === "running applications snapshot",
		);
		const applications = applicationsEvent?.details?.applications as string[] | undefined;

		return Array.isArray(applications) ? applications : [];
	}, [events]);

	// Shared with TimestampLinks so the keystrokes/apps-activity/audio-transcript availability, download
	// handlers, and (in particular) the audio-transcript data-dump fetch only need to exist in one place —
	// see useEventDownloads' `transcripts` param for how the fetch itself is deduplicated when nested.
	const {
		isDownloadKeystrokesAvailable,
		isDownloadAppActivityAvailable,
		isDownloadAudioTranscriptAvailable,
		handleDownloadKeystrokes,
		handleDownloadApplicationActivity,
		handleDownloadAudioTranscript,
		isAudioTranscriptDownloading,
		audioTranscriptPortal,
	} = useEventDownloads({
		events,
		offsetGetter,
		duration,
		assessmentName,
		studentName,
		sessionId,
		courseName,
		attemptNumber,
		assessmentStatus,
		recordingDate,
		transcripts,
	});

	const showMenu =
		!!reportProps ||
		!!createEventProps ||
		!!deleteAttemptProps ||
		[isDownloadKeystrokesAvailable, isDownloadAppActivityAvailable, isDownloadAudioTranscriptAvailable].filter((x) => x)
			.length > 1 ||
		installedExtensions.length > 0 ||
		runningApplications.length > 0;

	if (!showMenu) {
		return null;
	}

	return (
		<>
			{/* Rendered unconditionally (not gated by menuShowing) so report-generation state/DOM persists
			    across the menu closing and reopening. */}
			{reportGenerator.state !== "notGenerated" && (
				<div aria-hidden style={{ width: "918px", left: "-1000000px", position: "absolute", pointerEvents: "none" }}>
					{reportGenerator.enhancedComponent}
				</div>
			)}
			<CoreMenuButton
				menuShowing={downloadMenuShowing}
				setMenuShowing={setDownloadMenuShowing}
				sx={{
					width: 44,
					height: 38,
					color: "#333333",
					...(boxed
						? {
								border: "1px solid #E0E0E0",
								borderRadius: "5px",
							}
						: {
								border: "none",
								padding: "0",
								width: "fit-content",
							}),
					"&:active": {
						backgroundColor: "#E9D5FF",
					},
					...(downloadMenuShowing && {
						backgroundColor: "#E9D5FF",
					}),
				}}
				slotProps={{
					menu: {
						sx: {
							borderRadius: "7px",
						},
					},
				}}
			>
				{createEventProps && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							createEventProps.pausePlayback();
							setCreateEventModalOpen(true);
						}}
						aria-haspopup="dialog"
					>
						<ListItemText>{i18n.t("result.components.customEventModalTSX.createEvent")}</ListItemText>
					</CoreMenuItem>
				)}
				{reportProps && (
					<PDFReportMenuItems
						state={reportGenerator.state}
						buttonText={reportGenerator.buttonText}
						onButtonClick={reportGenerator.handleButtonClick}
						onRerender={reportGenerator.rerenderComponent}
					/>
				)}
				{isDownloadKeystrokesAvailable && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							handleDownloadKeystrokes();
						}}
					>
						<ListItemText>{i18n.t("result.components.resultsPage.downloadKeystrokes")}</ListItemText>
					</CoreMenuItem>
				)}
				{isDownloadAppActivityAvailable && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							handleDownloadApplicationActivity();
						}}
					>
						<ListItemText>{i18n.t("result.components.resultsPage.downloadAppsActivity")}</ListItemText>
					</CoreMenuItem>
				)}
				{isDownloadAudioTranscriptAvailable && (
					// Unlike the two above, this one renders a PDF before anything is saved, so it reports
					// progress the same way the report item does: the menu stays open and the item goes
					// disabled with trailing dots until the file is handed to the browser.
					<CoreMenuItem onClick={handleDownloadAudioTranscript} disabled={isAudioTranscriptDownloading}>
						<ListItemText>
							{isAudioTranscriptDownloading
								? `${i18n.t("result.components.resultsPage.proctoringResultsReport.downloading")}...`
								: i18n.t("result.components.resultsPage.downloadAudioTranscript")}
						</ListItemText>
					</CoreMenuItem>
				)}
				{installedExtensions.length > 0 && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							setExtensionModalOpen(true);
						}}
					>
						<ListItemText>{i18n.t("result.components.resultsPage.viewInstalledBrowserExtensions")}</ListItemText>
					</CoreMenuItem>
				)}
				{runningApplications.length > 0 && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							setRunningApplicationsModalOpen(true);
						}}
					>
						<ListItemText>{i18n.t("result.components.resultsPage.viewRunningApplications")}</ListItemText>
					</CoreMenuItem>
				)}
				{/* Last, and unconditional once the caller allows it at all — every item above appears only
				    when the session actually has that data. */}
				{deleteAttemptProps && (
					<CoreMenuItem
						onClick={() => {
							setDownloadMenuShowing(false);
							setDeleteAttemptModalOpen(true);
						}}
						aria-haspopup="dialog"
					>
						<ListItemText>{i18n.t("result.components.resultsPage.deleteAttemptModal.title")}</ListItemText>
					</CoreMenuItem>
				)}
			</CoreMenuButton>
			<InstalledExtensionsModal
				open={extensionModalOpen}
				onClose={() => setExtensionModalOpen(false)}
				extensions={installedExtensions}
			/>
			<RunningApplicationsModal
				open={runningApplicationsModalOpen}
				onClose={() => setRunningApplicationsModalOpen(false)}
				applications={runningApplications}
			/>
			{createEventProps && createEventModalOpen && (
				<CreateCustomEventModal
					onClose={() => setCreateEventModalOpen(false)}
					onSave={createEventProps.onSave}
					displayedTime={createEventProps.displayedTime}
				/>
			)}
			{deleteAttemptProps && deleteAttemptModalOpen && (
				<DeleteAttemptModal
					attemptNumber={deleteAttemptProps.attemptNumber}
					studentName={deleteAttemptProps.studentName}
					onClose={() => setDeleteAttemptModalOpen(false)}
					onDelete={deleteAttemptProps.onDelete}
				/>
			)}
			{audioTranscriptPortal}
		</>
	);
}
